import { MutationCache, QueryCache, QueryClient, type Query } from '@tanstack/react-query';
import { ApiError } from '@tradeoutlet/shared';

/** The API answers 403 { code: 'password_change_required' } while an admin-set temporary password is in use. */
export const isPasswordChangeRequired = (err: unknown) =>
  err instanceof ApiError && err.status === 403 && (err.body as { code?: string } | null)?.code === 'password_change_required';
const apiCode = (err: unknown) => (err instanceof ApiError && err.status === 403 ? (err.body as { code?: string } | null)?.code : undefined);
// Refetching the security status makes <App> swap to the forced-change screen (features/auth/password.tsx), or (t413u)
// to two-factor set-up; a session that still needs its code goes back to the code step.
const onApiError = (err: unknown) => {
  const code = apiCode(err);
  if (code === 'password_change_required' || code === 'mfa_enrolment_required') void queryClient.invalidateQueries({ queryKey: ['me', 'security'] });
  else if (code === 'mfa_required') void import('./api').then(m => m.onMfaRequired());
};

/**
 * UI review: most screens don't render their own query error, so a load that fails outright (network or server
 * error, no cached data to show) raises one toast. 4xx answers are the screen's business (access, not found).
 * Opt out with `meta: { silent: true }`.
 */
let lastLoadToast = 0;
export function shouldToastLoadError(err: unknown, query: { state: { data: unknown }; meta?: Record<string, unknown> }): boolean {
  if (query.meta?.silent || query.state.data !== undefined) return false;
  return !(err instanceof ApiError && err.status < 500);
}
const onQueryError = (err: unknown, query: Query<unknown, unknown, unknown>) => {
  onApiError(err);
  if (!shouldToastLoadError(err, query) || Date.now() - lastLoadToast < 15_000) return;
  lastLoadToast = Date.now();
  void import('./store').then(m => m.useUi.getState().toast('Couldn’t load part of this page. Check your connection; it will retry when you come back to it.', 'error'));
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onQueryError }),
  mutationCache: new MutationCache({ onError: onApiError }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      // t269u: the API client already retried 502/503/504 and dropped connections for ~15–20 s; don't stack more on top.
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && ((err as { attempts?: number } | null)?.attempts ?? 1) <= 1 && count < 2,
    },
  },
});

/** Query keys live in the shared data layer (packages/data) so the web and native apps agree. */
export { qk } from '@tradeoutlet/data';
